import React, { useState, useEffect } from 'react';
import { locationsApi, cashiersApi } from '../utils/api';
import { Typography, Button, Input, Table, Alert, Card, Form, Tag, Select, Spin, Grid } from 'antd';
import ColumnsCardList from '../components/common/ColumnsCardList';
import { PlusOutlined, CheckOutlined, CloseOutlined, ShopOutlined } from '@ant-design/icons';
import { Popconfirm } from 'antd';

const { Title } = Typography;
const { Option } = Select;

const Cashiers = () => {
  // Mobile (< md) : cartes à la place du tableau ; `=== false` évite un flash avant la mesure
  const screens = Grid.useBreakpoint();
  const isMobile = screens.md === false;
  const [cashiers, setCashiers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingLocations, setLoadingLocations] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [form] = Form.useForm();

  // Charge ensemble les caissiers et les points de vente du contexte actif.
  useEffect(() => {
    fetchCashiers();
    fetchLocations();
  }, []);

  /** Récupère les caissiers déjà limités à la boutique e-commerce active. */
  const fetchCashiers = async () => {
    try {
      setLoading(true);
      const response = await cashiersApi.getAll();
      setCashiers(response.data || []);
      setError(null);
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Impossible de récupérer la liste des caissiers.';
      setError(errorMsg);
      console.error('Erreur lors de la récupération des caissiers:', err.response?.data || err.message);
    } finally {
      setLoading(false);
    }
  };

  /** Récupère les points de vente utilisables pour l'affectation des caissiers. */
  const fetchLocations = async () => {
    try {
      setLoadingLocations(true);
      const response = await locationsApi.getAll({ is_active: true });
      setLocations(response.data || []);
    } catch (err) {
      console.error('Erreur lors de la récupération des points de vente:', err.response?.data || err.message);
    } finally {
      setLoadingLocations(false);
    }
  };

  /** Crée un caissier pour le point de vente sélectionné dans le formulaire. */
  const handleAddCashier = async (values) => {
    try {
      setLoading(true);
      await cashiersApi.create({
        name: values.name,
        pinCode: values.pinCode,
        locationId: values.locationId
      });
      setSuccess('Caissier ajouté avec succès');
      setError(null);
      form.resetFields();
      fetchCashiers();
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Erreur lors de l\'ajout du caissier';
      setError(errorMsg);
      setSuccess(null);
      console.error('Erreur lors de l\'ajout du caissier:', err.response?.data || err.message);
    } finally {
      setLoading(false);
    }
  };

  /** Active ou désactive un caissier sans modifier son point de vente. */
  const handleToggleActive = async (id, isActive) => {
    try {
      setLoading(true);
      console.log(`Tentative de mise à jour du caissier ID: ${id} vers is_active: ${!isActive}`);
      var data = await cashiersApi.update(id, { is_active: !isActive });
      console.log(data);
      setSuccess(`Caissier ${isActive ? 'désactivé' : 'activé'} avec succès`);
      setError(null);
      fetchCashiers();
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Erreur lors de la mise à jour du statut du caissier';
      setError(errorMsg);
      setSuccess(null);
      console.error('Erreur lors de la mise à jour du caissier:', err.response?.data || err.message);
    } finally {
      setLoading(false);
    }
  };

  /** Formate une date de création de façon lisible dans le tableau. */
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const columns = [
    {
      title: 'Nom',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      width: window.innerWidth < 768 ? 120 : undefined,
      render: (text) => (
        <span className={`font-medium ${window.innerWidth < 768 ? 'text-sm' : ''}`}>
          {text}
        </span>
      ),
    },
    {
      title: 'Point de vente',
      dataIndex: 'location_name',
      key: 'location_name',
      render: (locationName, record) => (
        <div className="min-w-0">
          {locationName ? (
            <span className={`truncate block ${window.innerWidth < 768 ? 'text-xs' : 'text-sm'}`}>
              {locationName}
            </span>
          ) : (
            <Tag size={window.innerWidth < 768 ? 'small' : 'default'}>
              Non assigné
            </Tag>
          )}
        </div>
      ),
      filters: locations.map(location => ({ text: location.name, value: location.id })),
      onFilter: (value, record) => record.location_id === value,
      sorter: (a, b) => (a.location_name || '').localeCompare(b.location_name || ''),
      width: window.innerWidth < 768 ? 100 : undefined,
      responsive: window.innerWidth < 768 ? ['sm'] : undefined,
    },
    {
      title: 'Statut',
      dataIndex: 'is_active',
      key: 'is_active',
      filters: [
        { text: 'Actif', value: true },
        { text: 'Inactif', value: false },
      ],
      onFilter: (value, record) => record.is_active === value,
      render: (isActive) => (
        <Tag 
          color={isActive ? 'success' : 'error'}
          size={window.innerWidth < 768 ? 'small' : 'default'}
        >
          {isActive ? 'Actif' : 'Inactif'}
        </Tag>
      ),
      width: window.innerWidth < 768 ? 70 : undefined,
    },
    {
      title: 'Créé le',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => (
        <span className={window.innerWidth < 768 ? 'text-xs' : 'text-sm'}>
          {formatDate(date)}
        </span>
      ),
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
      responsive: window.innerWidth < 768 ? ['md'] : undefined,
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: window.innerWidth < 768 ? 80 : undefined,
      render: (_, record) => (
        <Popconfirm
          title={`Êtes-vous sûr de vouloir ${record.is_active ? 'désactiver' : 'activer'} ce caissier ?`}
          onConfirm={() => handleToggleActive(record.id, record.is_active)}
          okText="Oui"
          cancelText="Non"
          disabled={loading}
        >
          <Button
            type="primary"
            ghost={record.is_active}
            danger={record.is_active}
            icon={record.is_active ? <CloseOutlined /> : <CheckOutlined />}
            loading={loading}
            size={window.innerWidth < 768 ? 'small' : 'middle'}
            className={window.innerWidth < 768 ? 'min-h-[32px]' : ''}
          >
            {window.innerWidth < 768 ? (
              record.is_active ? 'Désact.' : 'Act.'
            ) : (
              record.is_active ? 'Désactiver' : 'Activer'
            )}
          </Button>
        </Popconfirm>
      ),
    },
  ];

  const validatePinCode = (_, value) => {
    if (!value) {
      return Promise.reject(window.innerWidth < 768 ? 'PIN requis' : 'Le code PIN est requis');
    }
    if (!/^\d{6}$/.test(value)) {
      return Promise.reject(window.innerWidth < 768 ? 'PIN: 6 chiffres' : 'Le code PIN doit contenir exactement 6 chiffres');
    }
    return Promise.resolve();
  };

  return (
    <div className={`${window.innerWidth < 768 ? 'p-4' : 'p-6'}`}>
      <Title level={window.innerWidth < 768 ? 4 : 2} className={`${window.innerWidth < 768 ? 'text-lg mb-4' : 'text-2xl mb-6'}`}>
        <span className="hidden sm:inline">Gestion des </span>Caissiers
      </Title>

      {error && (
        <Alert
          message="Erreur"
          description={error}
          type="error"
          showIcon
          closable
          onClose={() => setError(null)}
          className={`mb-4 ${window.innerWidth < 768 ? 'text-sm' : ''}`}
        />
      )}
      {success && (
        <Alert
          message="Succès"
          description={success}
          type="success"
          showIcon
          closable
          onClose={() => setSuccess(null)}
          className={`mb-4 ${window.innerWidth < 768 ? 'text-sm' : ''}`}
        />
      )}

      <div className={`${
        window.innerWidth < 768 
          ? 'flex flex-col gap-4 mt-4' 
          : 'grid grid-cols-1 lg:grid-cols-3 gap-6 mt-4'
      }`}>
        <Card 
          title={<span className={window.innerWidth < 768 ? 'text-sm' : ''}>
            Ajouter un caissier
          </span>}
          className={window.innerWidth < 768 ? 'order-2 lg:order-1' : 'lg:col-span-1'}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleAddCashier}
            autoComplete="off"
          >
            <Form.Item
              name="name"
              label={<span className={window.innerWidth < 768 ? 'text-sm' : ''}>
                Nom du caissier
              </span>}
              rules={[{ required: true, message: 'Le nom du caissier est requis' }]}
            >
              <Input 
                placeholder={window.innerWidth < 768 ? 'Nom' : 'Nom du caissier'} 
                disabled={loading}
                size={window.innerWidth < 768 ? 'small' : 'middle'}
              />
            </Form.Item>

            <Form.Item
              name="pinCode"
              label={<span className={window.innerWidth < 768 ? 'text-sm' : ''}>
                Code PIN {window.innerWidth < 768 ? '' : '(6 chiffres)'}
              </span>}
              rules={[{ validator: validatePinCode }]}
            >
              <Input.Password
                placeholder={window.innerWidth < 768 ? 'PIN (6 chiffres)' : 'Code PIN à 6 chiffres'}
                maxLength={6}
                disabled={loading}
                size={window.innerWidth < 768 ? 'small' : 'middle'}
              />
            </Form.Item>

            <Form.Item
              name="locationId"
              label={<span className={window.innerWidth < 768 ? 'text-sm' : ''}>
                Point de vente {window.innerWidth < 768 ? '' : 'assigné'}
              </span>}
              rules={[{ required: true, message: 'Veuillez sélectionner un point de vente' }]}
            >
              <Select
                placeholder={window.innerWidth < 768 ? 'Point de vente' : 'Sélectionner un point de vente'}
                loading={loadingLocations}
                disabled={loadingLocations || loading}
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
                size={window.innerWidth < 768 ? 'small' : 'middle'}
              >
                {locations.map(location => (
                  <Option key={location.id} value={location.id}>
                    {location.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                icon={<PlusOutlined />}
                loading={loading}
                block
                size={window.innerWidth < 768 ? 'small' : 'middle'}
                className={window.innerWidth < 768 ? 'min-h-[44px]' : ''}
              >
                {window.innerWidth < 768 ? 'Ajouter' : 'Ajouter le caissier'}
              </Button>
            </Form.Item>
          </Form>
        </Card>

        <Card 
          title={<span className={window.innerWidth < 768 ? 'text-sm' : ''}>
            Liste des caissiers
          </span>}
          className={window.innerWidth < 768 ? 'order-1 lg:order-2' : 'lg:col-span-2'}
        >
          <Spin spinning={loading}>
            {isMobile ? (
              /* Mobile : une carte par caissier (nom en haut, infos, bouton activer/désactiver en bas) */
              <ColumnsCardList
                columns={columns}
                dataSource={cashiers}
                headerKeys={['name']}
                actionKeys={['actions']}
                clientPageSize={5}
                emptyText="Aucun caissier enregistré"
              />
            ) : (
            <Table
              columns={columns}
              dataSource={cashiers.map(c => ({...c, key: c.id}))}
              rowKey="id"
              loading={loading}
              pagination={{ 
                pageSize: window.innerWidth < 768 ? 5 : 10,
                showSizeChanger: window.innerWidth > 768,
                showQuickJumper: window.innerWidth > 768,
                showTotal: (total, range) => 
                  window.innerWidth > 768 
                    ? `${range[0]}-${range[1]} de ${total} caissiers`
                    : `${total} caissiers`
              }}
              locale={{ emptyText: 'Aucun caissier enregistré' }}
              scroll={{ 
                x: 'max-content',
                scrollToFirstRowOnChange: true
              }}
              size={window.innerWidth < 768 ? 'small' : 'middle'}
            />
            )}
          </Spin>
        </Card>
      </div>
    </div>
  );
};

export default Cashiers;
